import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Button,
  Row,
  Col,
} from "reactstrap";
import { Image } from "../../../AbstractElements";
import { dynamicImage } from "../../../Utils";
import moment from "moment";
import { getData, postData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import CommonTableNew from "../../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";
import MonthDropdown from "../../CommonCompo/MonthDropdown";
import { getLabel } from "../../../sharedFunctions/CommonLable";

interface UserExpensesProps {
  employeeIdProp: number;
}

const UserExpenses: React.FC<UserExpensesProps> = ({ employeeIdProp }) => {
  const navigate = useNavigate();
  const serverUrl = localStorage.getItem("serverUrl") || "";

  const [loading, setLoading] = useState<boolean>(true);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedRange, setSelectedRange] = useState<string>("");

  const [expenseData, setExpenseData] = useState<any[]>([]);
  const [statusMap, setStatusMap] = useState<Record<number, string>>({});
  const [workingDays, setWorkingDays] = useState<number | null>(null);
  const [totalExpenseAmount, setTotalExpenseAmount] = useState<number>(0);

  const [lastDates, setLastDates] = useState({
    LastSubmittedDate: null,
    LastApprovedDate: null,
    LastAuditedDate: null,
  });

  const handleMonthChange = (formattedMonth: string, rawKey?: string) => {
    setSelectedMonth(formattedMonth);
    setSelectedRange(rawKey || "");
  };

  // Fetch Status Map on mount
  useEffect(() => {
    const fetchStatusMap = async () => {
      try {
        let serverBaseUrl = serverUrl;
        if (serverBaseUrl && !serverBaseUrl.endsWith("/")) {
          serverBaseUrl += "/";
        }

        const statusUrl = `${serverBaseUrl}${API.getStatus}`;
        const statusRes = await getData(statusUrl, true);
        if (statusRes.status === 200 && statusRes.data?.data?.[0]?.Expenses) {
          const mapObj = statusRes.data.data[0].Expenses;
          const parsed: Record<number, string> = {};
          Object.keys(mapObj).forEach((k) => {
            parsed[parseInt(k, 10)] = mapObj[k];
          });
          setStatusMap(parsed);
        }
      } catch (err) {
        console.error("Error fetching status map:", err);
      }
    };

    fetchStatusMap();
  }, [serverUrl]);

  // Fetch expenses for selected month and employee
  useEffect(() => {
    if (!selectedMonth && !selectedRange) return;

    const fetchMonthlyExpenses = async () => {
      setLoading(true);
      try {
        let serverBaseUrl = serverUrl;
        if (serverBaseUrl && !serverBaseUrl.endsWith("/")) {
          serverBaseUrl += "/";
        }

        const activeEmpId = employeeIdProp || Number(localStorage.getItem("empId")) || 0;
        const monthParam = selectedRange || selectedMonth;
        const formattedMonthParam =
          selectedMonth ||
          (selectedRange.includes("|")
            ? moment(selectedRange.split("|")[0], "YYYY-MM-DD").format("MM-YYYY")
            : selectedRange);

        // 1. Fetch Expenses List
        const listUrl = `${serverBaseUrl}${API.getExpensesNew}`;
        const listParams = {
          month: monthParam,
          employee: activeEmpId,
          employee_id: activeEmpId,
        };
        const listRes = await postData(listUrl, true, listParams);

        const rawObj =
          listRes.status === 200 && listRes.data?.data?.data
            ? listRes.data.data.data
            : {};

        // Directly format all available expense data from the API
        let formattedData: any[] = [];
        if (Array.isArray(rawObj)) {
          formattedData = rawObj;
        } else if (rawObj && typeof rawObj === "object") {
          formattedData = Object.entries(rawObj).map(([dateKey, val]: [string, any]) => ({
            date: val?.date || val?.expenses?.ExpenseDate || dateKey,
            ...val,
          }));
        }

        // Sort chronologically by date
        formattedData.sort((a, b) => {
          return moment(a.date, "YYYY-MM-DD").valueOf() - moment(b.date, "YYYY-MM-DD").valueOf();
        });

        setExpenseData(formattedData);

        // Calculate total expense amount from actual data
        const total = formattedData.reduce((acc, item) => {
          if (item?.expenses?.ExpenseFinalAmt != null) {
            return acc + (Number(item.expenses.ExpenseFinalAmt) || 0);
          }
          if (item?.TotalExpenseAmount != null) {
            return acc + (Number(item.TotalExpenseAmount) || 0);
          }
          if (Array.isArray(item.expenseLine)) {
            const lineSum = item.expenseLine.reduce(
              (s: number, l: any) => s + (Number(l.ExpFinalAmount) || 0),
              0
            );
            return acc + lineSum;
          }
          return acc;
        }, 0);
        setTotalExpenseAmount(total);

        // 2. Fetch Working Days
        const workingDaysUrl = `${serverBaseUrl}${API.getWorkingDays}`;
        const workingDaysParams = { employee_id: activeEmpId, month: formattedMonthParam };
        const wdRes = await getData(workingDaysUrl, true, workingDaysParams);
        if (wdRes.status === 200) {
          setWorkingDays(wdRes.data?.data?.WorkingDays ?? null);
        }

        // 3. Fetch Last Dates
        const lastDatesUrl = `${serverBaseUrl}${API.getExpenseLastDates}`;
        const lastDatesParams = { employee_id: activeEmpId, month: monthParam };
        const ldRes = await getData(lastDatesUrl, true, lastDatesParams);
        if (ldRes.status === 200 && ldRes.data?.data) {
          setLastDates(ldRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching expense data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlyExpenses();
  }, [selectedMonth, selectedRange, employeeIdProp, serverUrl]);

  // Handle View click on row -> navigate to Expense Overview page
  const handleViewDetails = (item: any, index: number) => {
    const selectedExpId = item.expenses?.ExpId;
    if (!selectedExpId) return;

    const validExpenseList = expenseData.filter((i) => i?.expenses?.ExpId);
    const validIndex = validExpenseList.findIndex(
      (i) => i?.expenses?.ExpId === selectedExpId
    );

    navigate("/expenses/expenseoverview", {
      state: {
        expId: selectedExpId,
        doVerify: item.expenses?.DoVerify || false,
        expenseStatus: item.expenses?.ExpenseStatus,
        expenseList: validExpenseList,
        currentIndex: validIndex >= 0 ? validIndex : 0,
        isOwnUser: true,
        manager: false,
      },
    });
  };

  const getRowColor = (item: any) => {
    if (item?.expenses?.DoVerify) {
      return "#FFF9E6";
    }
    if (item?.WeekOff || item?.Holiday || item?.Leave || item?.Day === "Sunday") {
      return "#E6F4FA";
    }
    if (item?.FW < 4 || (item?.NCA > 0 && item?.FW > 0)) {
      return "#FDE8E8";
    }
    return "#FFFFFF";
  };

  const headData = [
    { id: "select", head: "", key: "select" },
    { id: "date", head: "Expense Date", key: "date" },
    { id: "totalCalls", head: "Total Calls", key: "TotalCalls" },
    { id: "totalNCAs", head: `Total ${getLabel('NCA')}'s`, key: "NCA" },
    { id: "type", head: "Type", key: "expenses.TripType" },
    { id: "placeOfWork", head: "Place Of Work", key: "expenses.ExpensePlacewrk" },
    { id: "ta", head: "TA", key: "ta" },
    { id: "da", head: "DA", key: "da" },
    { id: "otherExpense", head: "Other Expense", key: "otherExpense" },
    { id: "totalExpense", head: "Total Expense", key: "expenses.ExpenseFinalAmt" },
    { id: "status", head: "Status", key: "expenses.ExpenseStatus" },
    { id: "action", head: "", key: "action" },
  ];

  return (
    <div className="p-3">
      {/* Header & Month Selector */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="m-0 fw-bold text-dark">
          <i className="fa fa-money me-2 text-primary"></i> Expenses Overview
        </h4>
        <MonthDropdown onChange={handleMonthChange} />
      </div>

      {/* Summary Statistic Cards */}
      <Row className="g-3 mb-4">
        <Col lg={3} md={6} sm={12}>
          <Card className="border shadow-sm mb-0">
            <CardBody className="p-3">
              <div className="text-muted small fw-semibold">Total Expenses</div>
              <h4 className="m-0 fw-bold text-primary mt-1">₹ {totalExpenseAmount.toLocaleString()}</h4>
            </CardBody>
          </Card>
        </Col>

        <Col lg={3} md={6} sm={12}>
          <Card className="border shadow-sm mb-0">
            <CardBody className="p-3">
              <div className="text-muted small fw-semibold">Working Days</div>
              <h4 className="m-0 fw-bold text-dark mt-1">{workingDays !== null ? workingDays : "-"}</h4>
            </CardBody>
          </Card>
        </Col>

        {lastDates.LastSubmittedDate && (
          <Col lg={3} md={6} sm={12}>
            <Card className="border shadow-sm mb-0">
              <CardBody className="p-3">
                <div className="text-muted small fw-semibold">Submitted Date</div>
                <h5 className="m-0 fw-bold text-dark mt-1">
                  {moment(lastDates.LastSubmittedDate).format("DD MMM YYYY")}
                </h5>
              </CardBody>
            </Card>
          </Col>
        )}

        {lastDates.LastApprovedDate && (
          <Col lg={3} md={6} sm={12}>
            <Card className="border shadow-sm mb-0">
              <CardBody className="p-3">
                <div className="text-muted small fw-semibold">Approved Date</div>
                <h5 className="m-0 fw-bold text-success mt-1">
                  {moment(lastDates.LastApprovedDate).format("DD MMM YYYY")}
                </h5>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>

      {/* Expenses Table */}
      {loading ? (
        <div className="text-center py-3" style={{ minHeight: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div>
            <Image
              className="b-r-10 img-80"
              src={dynamicImage("dashboard/loader.gif")}
              alt="Loading..."
            />
          </div>
        </div>
      ) : (
        <div className="border rounded bg-white shadow-sm overflow-hidden table-responsive">
          <CommonTableNew
            tableClass="table caption align-middle mb-0"
            strip={false}
            caption=""
            size="sm"
            hover
            hidePagination={true}
            headData={headData}
            data={expenseData}
            renderRow={(item: any, index: number) => {
              const hasType = item?.hasOwnProperty("Type");
              const ta =
                item.expenseLine?.find((l: any) =>
                  l.ExpenseMaster?.ExpenseName?.toLowerCase().includes("ta")
                )?.ExpFinalAmount || item.ta || 0;
              const da =
                item.expenseLine?.find((l: any) =>
                  l.ExpenseMaster?.ExpenseName?.toLowerCase().includes("da")
                )?.ExpFinalAmount || item.da || 0;
              const totalAmt = item.expenses?.ExpenseFinalAmt || item.TotalExpenseAmount || 0;
              const otherExpense = totalAmt > 0 ? totalAmt - (Number(ta) + Number(da)) : 0;
              const expenseStatus = Number(item.expenses?.ExpenseStatus);
              const statusLabel = !hasType && expenseStatus
                ? statusMap[expenseStatus] || "Submitted"
                : item.Day || "-";

              return (
                <tr key={index} style={{ backgroundColor: getRowColor(item) }}>
                  <td className="px-3" style={{ width: "30px" }}>
                    <input type="checkbox" disabled />
                  </td>
                  <td className="fw-semibold px-3">
                    {moment(item.date, "YYYY-MM-DD").format("DD MMM")}
                  </td>
                  <td className="px-3">{item.TotalCalls || "-"}</td>
                  <td className="px-3">{item.NCA || "-"}</td>
                  <td className="px-3">{item.expenses?.TripType || "-"}</td>
                  <td className="px-3">{item.expenses?.ExpensePlacewrk || "-"}</td>
                  <td className="px-3">{ta}</td>
                  <td className="px-3">{da}</td>
                  <td className="px-3">{otherExpense > 0 ? otherExpense : "-"}</td>
                  <td className="fw-bold px-3">{totalAmt > 0 ? totalAmt : "-"}</td>
                  <td className="px-3">{statusLabel}</td>
                  <td className="px-3 text-end">
                    {!hasType && item?.expenses?.ExpId ? (
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => handleViewDetails(item, index)}
                      >
                        View
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            }}
          />
        </div>
      )}
    </div>
  );
};

export default UserExpenses;
